import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Bell, X, Users, Monitor, Settings, Lock, Unlock, Laptop, ChevronDown, UserCheck } from 'lucide-react';
import FullscreenButton from '@/components/FullscreenButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apparatusNames = {
  vault: 'VAULT', wagvault: 'VAULT', magvault: 'VAULT',
  bars: 'UNEVEN BARS',
  beam: 'BALANCE BEAM',
  floor: 'FLOOR', wagfloor: 'FLOOR', magfloor: 'FLOOR',
  range: 'RANGE & COND.',
  pommel: 'POMMEL', rings: 'RINGS', pbars: 'PARALLEL BARS', highbar: 'HIGH BAR',
};

const ROTATIONS = [1,2,3,4,5,6,7,8,9,10,11,12];

export default function OpenDashboardPage() {
  // ── PIN gate ────────────────────────────────────────────────────────────────
  const [pinEntered, setPinEntered] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');

  // ── Competition / Round / Rotation selection ─────────────────────────────────
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompId, setSelectedCompId] = useState('');
  const [selectedCompTitle, setSelectedCompTitle] = useState('');
  const [availableRounds, setAvailableRounds] = useState([]);
  const [rotationsByRound, setRotationsByRound] = useState({});
  const [availableRotations, setAvailableRotations] = useState([]);
  const [selectedRound, setSelectedRound] = useState('');
  const [selectedRotation, setSelectedRotation] = useState('');
  const [selectionDone, setSelectionDone] = useState(false);

  // ── Live data ───────────────────────────────────────────────────────────────
  const [alerts, setAlerts] = useState([]);
  const [isFlashing, setIsFlashing] = useState(false);
  const prevAlertsCountRef = useRef(0);
  const audioRef = useRef(null);
  const [checkInStats, setCheckInStats] = useState(null);
  const [checkInGymnasts, setCheckInGymnasts] = useState([]);
  const [expandedSection, setExpandedSection] = useState(null); // 'checkedIn'|'yetToCheckIn'|'withdrawn'
  const [withdrawing, setWithdrawing] = useState(null); // gymnast id being withdrawn
  const [rotationStatus, setRotationStatus] = useState([]);
  const [displayControlMode, setDisplayControlMode] = useState('normal');
  const [savingControl, setSavingControl] = useState(false);

  // Terminal lock state
  const [terminalOneLocked, setTerminalOneLocked] = useState(false);
  const [terminalTwoLocked, setTerminalTwoLocked] = useState(false);
  const [scoringLocked, setScoringLocked] = useState(false);

  // ── Coaches state ────────────────────────────────────────────────────────────
  const [coaches, setCoaches] = useState([]);
  const [coachExpanded, setCoachExpanded] = useState(null); // 'arrived'|'notArrived'|'walkIn'
  const [togglingCoach, setTogglingCoach] = useState(null); // coach id being toggled

  const isActive = !!(selectionDone && selectedCompId && selectedRound && selectedRotation);

  const apparatusToLabel = {
    wagvault: 'VT', vault: 'VT', magvault: 'VT',
    bars: 'UB', wagbars: 'UB',
    beam: 'BB', wagbeam: 'BB',
    wagfloor: 'FX', floor: 'FX', magfloor: 'FX',
  };

  const isApparatusFinished = (panelLetter, appLabel) =>
    rotationStatus.some(s => {
      const label = apparatusToLabel[s.apparatus];
      const panelMatch = s.panel === panelLetter || s.panel === 'single';
      return label === appLabel && panelMatch;
    });

  // ── Load competitions after PIN ──────────────────────────────────────────────
  useEffect(() => {
    if (!pinEntered) return;
    axios.get(`${API}/music/competitions`)
      .then(res => setCompetitions(res.data?.competitions || []))
      .catch(() => {});
  }, [pinEntered]);

  // ── Load rounds & rotation counts when competition changes ──────────────────
  useEffect(() => {
    if (!selectedCompId) {
      setAvailableRounds([]);
      setRotationsByRound({});
      setAvailableRotations([]);
      setSelectedRound('');
      setSelectedRotation('');
      return;
    }
    axios.get(`${API}/competition-structure/${selectedCompId}`)
      .then(res => {
        const rounds = res.data?.rounds || [];
        const byRound = res.data?.rotationsByRound || {};
        setAvailableRounds(rounds.length ? rounds : [1, 2, 3]);
        setRotationsByRound(byRound);
      })
      .catch(() => {
        setAvailableRounds([1, 2, 3]);
        setRotationsByRound({});
      });
    setSelectedRound('');
    setSelectedRotation('');
  }, [selectedCompId]);

  // ── Update available rotations when round changes ────────────────────────────
  useEffect(() => {
    if (!selectedRound) { setAvailableRotations([]); setSelectedRotation(''); return; }
    const count = rotationsByRound[parseInt(selectedRound)] || 0;
    setAvailableRotations(count > 0 ? Array.from({ length: count }, (_, i) => i + 1) : Array.from({ length: 12 }, (_, i) => i + 1));
    setSelectedRotation('');
  }, [selectedRound, rotationsByRound]);

  const handleCompetitionChange = (id) => {
    const comp = competitions.find(c => c.id === id);
    setSelectedCompId(id);
    setSelectedCompTitle(comp?.title || '');
  };

  const handleOpenDashboard = () => {
    if (!selectedCompId || !selectedRound || !selectedRotation) return;
    // Persist selection to backend so self-registration terminals can read it
    axios.put(`${API}/open-state`, {
      isActive: true,
      competitionId: selectedCompId,
      competitionTitle: selectedCompTitle,
      roundNumber: selectedRound,
      rotationNumber: selectedRotation,
    }).catch(() => {});
    setSelectionDone(true);
  };

  // ── Poll help alerts (every 1 s) ─────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) { setAlerts([]); setIsFlashing(false); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/help-alerts/${selectedCompId}`);
        const newAlerts = res.data.alerts || [];
        if (newAlerts.length > prevAlertsCountRef.current) {
          setIsFlashing(true);
          audioRef.current?.play().catch(() => {});
        }
        prevAlertsCountRef.current = newAlerts.length;
        setAlerts(newAlerts);
        if (newAlerts.length === 0) setIsFlashing(false);
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 1000);
    return () => clearInterval(id);
  }, [isActive, selectedCompId]);

  // ── Poll check-in stats (every 5 s) ──────────────────────────────────────────
  useEffect(() => {
    if (!isActive) { setCheckInStats(null); setCheckInGymnasts([]); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/registration/status/${selectedCompId}/${selectedRound}`);
        setCheckInStats(res.data.summary || null);
        setCheckInGymnasts(res.data.gymnasts || []);
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [isActive, selectedCompId, selectedRound]);

  // ── Load coaches (once on comp select, then every 10 s) ──────────────────────
  const loadCoaches = async (compId) => {
    if (!compId) { setCoaches([]); return; }
    try {
      const res = await axios.get(`${API}/registration/coaches/${compId}`);
      setCoaches(res.data.coaches || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (!selectedCompId) { setCoaches([]); return; }
    loadCoaches(selectedCompId);
    const id = setInterval(() => loadCoaches(selectedCompId), 10000);
    return () => clearInterval(id);
  }, [selectedCompId]);

  const handleCoachCheckIn = async (coach) => {
    setTogglingCoach(coach.id);
    try {
      await axios.post(`${API}/registration/coach-check-in`, { competitionId: selectedCompId, coachId: coach.id });
      await loadCoaches(selectedCompId);
    } catch (_) {}
    setTogglingCoach(null);
  };

  const handleUndoCoachCheckIn = async (coach) => {
    setTogglingCoach(coach.id);
    try {
      await axios.delete(`${API}/registration/coach-check-in/${selectedCompId}/${coach.id}`);
      await loadCoaches(selectedCompId);
    } catch (_) {}
    setTogglingCoach(null);
  };

  // ── Poll rotation finished status (every 3 s) ────────────────────────────────
  useEffect(() => {
    if (!isActive) { setRotationStatus([]); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/rotation-status/${selectedCompId}/${selectedRound}/${selectedRotation}`);
        setRotationStatus(res.data.finished || []);
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [isActive, selectedCompId, selectedRound, selectedRotation]);

  // ── Poll display control mode (every 3 s) ────────────────────────────────────
  useEffect(() => {
    if (!isActive) { setDisplayControlMode('normal'); return; }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/display-control/${selectedCompId}`);
        setDisplayControlMode(res.data?.mode || 'normal');
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [isActive, selectedCompId]);

  const handleSetDisplayMode = async (mode) => {
    if (!selectedCompId) return;
    setSavingControl(true);
    try {
      await axios.put(`${API}/display-control`, { competitionId: selectedCompId, mode });
      setDisplayControlMode(mode);
    } catch (_) {}
    setSavingControl(false);
  };

  // ── Poll terminal lock state (every 5 s when active) ─────────────────────────
  useEffect(() => {
    if (!isActive) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/open-state`);
        setTerminalOneLocked(res.data?.terminalOneLocked || false);
        setTerminalTwoLocked(res.data?.terminalTwoLocked || false);
        setScoringLocked(res.data?.scoringLocked || false);
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [isActive]);

  const handleToggleLock = async (terminalId) => {
    const isOne = terminalId === 'terminalone';
    const currentLocked = isOne ? terminalOneLocked : terminalTwoLocked;
    const newLocked = !currentLocked;
    if (isOne) setTerminalOneLocked(newLocked);
    else setTerminalTwoLocked(newLocked);
    try {
      await axios.put(`${API}/open-state`, {
        [isOne ? 'terminalOneLocked' : 'terminalTwoLocked']: newLocked,
      });
    } catch (_) {
      if (isOne) setTerminalOneLocked(currentLocked);
      else setTerminalTwoLocked(currentLocked);
    }
  };

  const handleToggleScoringLock = async () => {
    const newLocked = !scoringLocked;
    setScoringLocked(newLocked);
    try {
      await axios.put(`${API}/open-state`, { scoringLocked: newLocked });
    } catch (_) {
      setScoringLocked(!newLocked); // revert
    }
  };

  const handleWithdraw = async (gymnast) => {
    setWithdrawing(gymnast.id);
    try {
      await axios.put(`${API}/open/withdraw/${selectedCompId}/${selectedRound}/${gymnast.id}`);
      const res = await axios.get(`${API}/registration/status/${selectedCompId}/${selectedRound}`);
      setCheckInStats(res.data.summary || null);
      setCheckInGymnasts(res.data.gymnasts || []);
    } catch (_) {}
    setWithdrawing(null);
  };

  const handleUnwithdraw = async (gymnast) => {
    setWithdrawing(gymnast.id);
    try {
      await axios.put(`${API}/open/unwithdraw/${selectedCompId}/${selectedRound}/${gymnast.id}`);
      const res = await axios.get(`${API}/registration/status/${selectedCompId}/${selectedRound}`);
      setCheckInStats(res.data.summary || null);
      setCheckInGymnasts(res.data.gymnasts || []);
    } catch (_) {}
    setWithdrawing(null);
  };

  const handleDismiss = async (alertId) => {
    try {
      await axios.delete(`${API}/help-alerts/${alertId}`);
      setAlerts(prev => {
        const remaining = prev.filter(a => a.id !== alertId);
        prevAlertsCountRef.current = remaining.length;
        if (remaining.length === 0) setIsFlashing(false);
        return remaining;
      });
    } catch (_) {}
  };

  // ── PIN gate screen ──────────────────────────────────────────────────────────
  if (!pinEntered) {
    return (
      <div className="h-screen bg-white flex items-center justify-center p-4">
        <Card className="w-full max-w-sm shadow-xl border-gray-200">
          <CardHeader className="text-center pb-4">
            <img src="/scorz-logo.png" alt="Scorz" className="h-14 mx-auto mb-3" />
            <CardTitle className="text-xl font-bold text-black">Open Dashboard</CardTitle>
            <p className="text-gray-500 text-sm">Competition day overview</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (pin === '2507') { setPinEntered(true); setPinError(''); }
              else { setPinError('Invalid PIN'); setPin(''); }
            }} className="space-y-4">
              <Input
                type="password"
                value={pin}
                onChange={(e) => { setPin(e.target.value); setPinError(''); }}
                placeholder="Enter PIN"
                className="text-center text-2xl tracking-widest"
                maxLength={4}
                autoFocus
                data-testid="open-pin-input"
              />
              {pinError && <p className="text-red-500 text-sm text-center">{pinError}</p>}
              <Button type="submit" className="w-full bg-black hover:bg-gray-800" data-testid="open-pin-submit">
                Access Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Selection screen ─────────────────────────────────────────────────────────
  if (!selectionDone) {
    return (
      <div className="h-screen bg-white flex items-center justify-center p-4">
        <Card className="w-full max-w-sm shadow-xl border-gray-200">
          <CardHeader className="text-center pb-4">
            <img src="/scorz-logo.png" alt="Scorz" className="h-12 mx-auto mb-3" />
            <CardTitle className="text-xl font-bold text-black">Open Dashboard</CardTitle>
            <p className="text-gray-500 text-sm">Select competition, round &amp; rotation</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select value={selectedCompId} onValueChange={handleCompetitionChange}>
              <SelectTrigger data-testid="open-competition-select">
                <SelectValue placeholder="Select competition" />
              </SelectTrigger>
              <SelectContent>
                {competitions.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={selectedRound}
              onValueChange={setSelectedRound}
              disabled={!selectedCompId}
            >
              <SelectTrigger data-testid="open-round-select">
                <SelectValue placeholder="Select round" />
              </SelectTrigger>
              <SelectContent>
                {availableRounds.map(r => (
                  <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={selectedRotation}
              onValueChange={setSelectedRotation}
              disabled={!selectedRound}
            >
              <SelectTrigger data-testid="open-rotation-select">
                <SelectValue placeholder="Select rotation" />
              </SelectTrigger>
              <SelectContent>
                {availableRotations.map(r => (
                  <SelectItem key={r} value={String(r)}>Rotation {r}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={handleOpenDashboard}
              disabled={!selectedCompId || !selectedRound || !selectedRotation}
              className="w-full bg-black hover:bg-gray-800 mt-2"
              data-testid="open-dashboard-btn"
            >
              Open Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Derive values ────────────────────────────────────────────────────────────
  const yetToCheckIn = checkInStats
    ? Math.max(0, (checkInStats.total || 0) - (checkInStats.checkedIn || 0) - (checkInStats.withdrawn || 0))
    : null;

  // ── Main 6-panel dashboard ───────────────────────────────────────────────────
  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Alert audio */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjiP1O/" />

      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200 shrink-0">
        <img src="/scorz-logo.png" alt="Scorz" className="h-7" />
        <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
          <span className="text-black font-semibold">{selectedCompTitle || 'Competition'}</span>
          <span className="text-gray-300">•</span>
          <span className="text-gray-700">Round {selectedRound}</span>
          <span className="text-gray-300">•</span>
          <span className="text-gray-700">Rotation {selectedRotation}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => {
              axios.put(`${API}/open-state`, { isActive: false }).catch(() => {});
              setSelectionDone(false);
              setAlerts([]);
              setCheckInStats(null);
              setRotationStatus([]);
            }}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-black border border-gray-200 hover:border-gray-400 rounded px-2 py-1 transition-colors"
            data-testid="open-change-selection"
            title="Change competition / round / rotation"
          >
            <Settings className="w-3 h-3" /> Change
          </button>
          <FullscreenButton />
        </div>
      </div>

      {/* 3-column 2-row grid */}
      <div className="flex-1 grid grid-cols-3 grid-rows-2 gap-1 p-1 min-h-0">

        {/* ── TOP-LEFT: Help Alerts ── */}
        <div
          data-testid="help-alerts-panel"
          className={`rounded-lg flex flex-col overflow-hidden transition-colors duration-300 ${
            isFlashing && alerts.length > 0
              ? 'bg-red-600 animate-pulse'
              : 'bg-white border border-gray-200'
          }`}
        >
          <div className={`flex items-center gap-2 px-3 py-2 border-b shrink-0 ${
            isFlashing && alerts.length > 0 ? 'border-red-500' : 'border-gray-200'
          }`}>
            <Bell className={`w-4 h-4 ${isFlashing && alerts.length > 0 ? 'text-white' : 'text-gray-500'}`} />
            <span className={`text-sm font-semibold uppercase tracking-wide ${
              isFlashing && alerts.length > 0 ? 'text-white' : 'text-gray-600'
            }`}>
              Help Alerts
            </span>
            {alerts.length > 0 && (
              <span className={`ml-auto text-sm font-bold px-2 py-0.5 rounded ${
                isFlashing ? 'bg-white text-red-600' : 'bg-red-600 text-white'
              }`}>
                {alerts.length}
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {alerts.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <Bell className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm">No active help alerts</p>
              </div>
            ) : (
              alerts.map(alert => (
                <div
                  key={alert.id}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                    isFlashing ? 'bg-white' : 'bg-red-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className={`w-6 h-6 shrink-0 ${isFlashing ? 'text-red-600' : 'text-white'}`} />
                    <div>
                      <p className={`font-bold text-sm ${isFlashing ? 'text-black' : 'text-white'}`}>
                        {apparatusNames[alert.apparatus] || alert.apparatus?.toUpperCase()}
                        {alert.panel && alert.panel !== 'single' && (
                          <span className={`ml-2 text-xs ${isFlashing ? 'text-gray-600' : 'text-yellow-300'}`}>
                            Panel {alert.panel}
                          </span>
                        )}
                      </p>
                      <p className={`text-xs ${isFlashing ? 'text-gray-500' : 'text-red-200'}`}>
                        {new Date(alert.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleDismiss(alert.id)}
                    className={`text-xs px-2 py-1 h-auto ${
                      isFlashing ? 'bg-black text-white hover:bg-gray-800' : 'bg-white text-black hover:bg-gray-100'
                    }`}
                    data-testid={`dismiss-alert-${alert.id}`}
                  >
                    <X className="w-3 h-3 mr-1" /> Dismiss
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── TOP-RIGHT: Check-In Stats + Apparatus Rows ── */}
        <div
          data-testid="checkin-stats-panel"
          className="rounded-lg bg-white border border-gray-200 flex flex-col overflow-hidden"
        >
          {/* Top half — check-in */}
          <div className="flex flex-col border-b border-gray-200 overflow-hidden min-h-0" style={{flex: expandedSection ? '1 1 0' : '0 0 auto'}}>
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 shrink-0">
              <Users className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Check-In — Round {selectedRound}
              </span>
            </div>

            {/* Stat tiles */}
            <div className="flex shrink-0 items-stretch divide-x divide-gray-100">
              {checkInStats ? (
                <>
                  {[
                    { key: 'checkedIn',    label: 'Checked In',       value: checkInStats.checkedIn,  color: 'text-green-600'  },
                    { key: 'yetToCheckIn', label: 'Yet to Check In',  value: yetToCheckIn,            color: 'text-yellow-500' },
                    { key: 'withdrawn',    label: 'Withdrawn',        value: checkInStats.withdrawn,  color: 'text-red-600'    },
                  ].map(({ key, label, value, color }) => (
                    <button
                      key={key}
                      data-testid={`checkin-expand-${key}`}
                      onClick={() => setExpandedSection(s => s === key ? null : key)}
                      className="flex-1 flex flex-col items-center py-2 gap-0.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className={`text-2xl font-black tabular-nums leading-none ${color}`}>{value}</div>
                      <span className={`text-[9px] font-semibold uppercase tracking-wide ${color}`}>{label}</span>
                      <ChevronDown className={`w-3 h-3 text-gray-300 transition-transform ${expandedSection === key ? 'rotate-180' : ''}`} />
                    </button>
                  ))}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center py-4">
                  <p className="text-xs text-gray-400">Loading...</p>
                </div>
              )}
            </div>

            {/* Expanded gymnast list */}
            {expandedSection && checkInStats && (
              <div className="flex-1 overflow-y-auto min-h-0 border-t border-gray-100">
                {(() => {
                  const g_list = checkInGymnasts.filter(g => {
                    if (expandedSection === 'checkedIn')    return g.checkInStatus?.checkedIn === true;
                    if (expandedSection === 'withdrawn')    return g.checkInStatus?.withdrawn === true;
                    if (expandedSection === 'yetToCheckIn') return !g.checkInStatus?.checkedIn && !g.checkInStatus?.withdrawn;
                    return false;
                  });
                  if (g_list.length === 0) return (
                    <p className="text-xs text-gray-400 text-center py-4">None</p>
                  );
                  return g_list.map(g => (
                    <div key={g.id} className="flex items-center justify-between px-3 py-1.5 border-b border-gray-50 last:border-0 hover:bg-gray-50">
                      <div className="min-w-0">
                        <span className="text-xs font-bold text-gray-900 truncate block">
                          {g.programmeNumber && <span className="text-gray-400 mr-1">#{g.programmeNumber}</span>}
                          {g.preferredFirstName || g.firstName} {g.preferredLastName || g.lastName}
                        </span>
                        <span className="text-[10px] text-gray-400 truncate block">{g.clubName}</span>
                      </div>
                      {expandedSection === 'yetToCheckIn' && (
                        <button
                          data-testid={`withdraw-btn-${g.id}`}
                          onClick={() => handleWithdraw(g)}
                          disabled={withdrawing === g.id}
                          className="ml-2 shrink-0 text-[10px] font-semibold px-2 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50 hover:border-red-400 disabled:opacity-40 transition-colors"
                        >
                          {withdrawing === g.id ? '...' : 'Withdraw'}
                        </button>
                      )}
                      {expandedSection === 'withdrawn' && (
                        <button
                          data-testid={`unwithdraw-btn-${g.id}`}
                          onClick={() => handleUnwithdraw(g)}
                          disabled={withdrawing === g.id}
                          className="ml-2 shrink-0 text-[10px] font-semibold px-2 py-1 rounded border border-green-200 text-green-600 hover:bg-green-50 hover:border-green-400 disabled:opacity-40 transition-colors"
                        >
                          {withdrawing === g.id ? '...' : 'Unwithdraw'}
                        </button>
                      )}
                    </div>
                  ));
                })()}
              </div>
            )}

            {checkInStats && !expandedSection && (
              <div className="px-3 pb-1 text-center shrink-0">
                <span className="text-[10px] text-gray-400">Total: {checkInStats.total}</span>
              </div>
            )}
          </div>

          {/* Bottom half — apparatus rows */}
          <div className="flex-1 flex flex-col justify-center gap-2 px-3 py-2 min-h-0 shrink-0">
            {['A', 'B'].map(panel => (
              <div key={panel} className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-gray-500 w-4 shrink-0">{panel}</span>
                {['VT', 'UB', 'BB', 'FX'].map(app => {
                  const done = isApparatusFinished(panel, app);
                  return (
                    <button
                      key={app}
                      className={`flex-1 py-1.5 rounded text-xs font-semibold border cursor-default transition-colors ${
                        done ? 'bg-green-600 border-green-600 text-white' : 'bg-gray-100 border-gray-200 text-black'
                      }`}
                      disabled
                      data-testid={`apparatus-btn-${panel}-${app}`}
                    >
                      {app}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* ── BOTTOM-LEFT: Terminal Control ── */}
        <div
          data-testid="terminal-control-panel"
          className="rounded-lg bg-white border border-gray-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 shrink-0">
            <Laptop className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Terminal Control</span>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-5 p-4">
            <p className="text-xs text-gray-400 text-center">Lock or open self-registration terminals</p>
            {[
              { id: 'terminalone', label: 'Terminal One', locked: terminalOneLocked },
              { id: 'terminaltwo', label: 'Terminal Two', locked: terminalTwoLocked },
            ].map(({ id, label, locked }) => (
              <div key={id} className="flex items-center justify-between w-full max-w-xs gap-3">
                <div>
                  <div className="font-semibold text-sm text-black">{label}</div>
                  <div className={`text-xs font-medium ${locked ? 'text-red-500' : 'text-green-600'}`}>
                    {locked ? 'Locked' : 'Open'}
                  </div>
                </div>
                <button
                  onClick={() => handleToggleLock(id)}
                  data-testid={`terminal-lock-${id}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    locked
                      ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                      : 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                  }`}
                >
                  {locked
                    ? <><Lock className="w-3.5 h-3.5" /> Locked</>
                    : <><Unlock className="w-3.5 h-3.5" /> Open</>}
                </button>
              </div>
            ))}

            {/* Scoring terminals lock */}
            <div className="w-full max-w-xs border-t border-gray-100 pt-4 mt-1">
              <p className="text-xs text-gray-400 text-center mb-3">Lock all scoring terminals</p>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-sm text-black">Scoring Terminals</div>
                  <div className={`text-xs font-medium ${scoringLocked ? 'text-red-500' : 'text-green-600'}`}>
                    {scoringLocked ? 'Locked — judges cannot score' : 'Open — judges can score'}
                  </div>
                </div>
                <button
                  onClick={handleToggleScoringLock}
                  data-testid="scoring-terminals-lock-btn"
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    scoringLocked
                      ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                      : 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                  }`}
                >
                  {scoringLocked
                    ? <><Lock className="w-3.5 h-3.5" /> Locked</>
                    : <><Unlock className="w-3.5 h-3.5" /> Open</>}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-2">
              Terminals auto-load: <span className="font-semibold">{selectedCompTitle || '—'}</span> · Round {selectedRound || '—'}
            </p>
          </div>
        </div>

        {/* ── BOTTOM-RIGHT: Display Control ── */}
        <div
          data-testid="display-control-panel"
          className="rounded-lg bg-white border border-gray-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 shrink-0">
            <Monitor className="w-3.5 h-3.5 text-gray-500" />
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Display Control</span>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-4">
            <p className="text-xs text-gray-400 text-center">Controls what appears on all venue display screens</p>
            <div className="flex flex-col gap-2 w-full max-w-xs">
              {[
                { key: 'normal',         label: 'Normal',          desc: 'Live scores & performers' },
                { key: 'sponsors',       label: 'Sponsors',        desc: 'Rotate sponsor logos every 10s' },
                { key: 'timer_sponsors', label: 'Timer + Sponsors',desc: 'Alternate timer & sponsors every 10s' },
                { key: 'sms_register',   label: 'SMS Register',    desc: 'QR code for spectator SMS registration' },
              ].map(({ key, label, desc }) => (
                <button
                  key={key}
                  data-testid={`display-mode-${key}`}
                  onClick={() => handleSetDisplayMode(key)}
                  disabled={savingControl}
                  className={`w-full px-4 py-3 rounded-lg text-left border transition-colors ${
                    displayControlMode === key
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-black border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-sm">{label}</div>
                  <div className={`text-xs mt-0.5 ${displayControlMode === key ? 'text-gray-300' : 'text-gray-500'}`}>{desc}</div>
                </button>
              ))}
            </div>
            {displayControlMode !== 'normal' && (
              <p className="text-[10px] text-gray-400 text-center">
                Sponsors are uploaded in competition settings
              </p>
            )}
          </div>
        </div>
        {/* ── RIGHT COLUMN (row-span-2): Coaches ── */}
        {(() => {
          const arrived   = coaches.filter(c => c.checkedIn && !c.walkin);
          const walkIns   = coaches.filter(c => c.walkin);
          const notArrived = coaches.filter(c => !c.checkedIn);

          const sectionCoaches = (key) => {
            if (key === 'arrived')    return arrived;
            if (key === 'notArrived') return notArrived;
            if (key === 'walkIn')     return walkIns;
            return [];
          };

          return (
            <div
              data-testid="coaches-panel"
              className="rounded-lg bg-white border border-gray-200 flex flex-col overflow-hidden col-start-3 row-start-1 row-span-2"
            >
              {/* Header */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 shrink-0">
                <UserCheck className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Coaches</span>
                {coaches.length > 0 && (
                  <span className="ml-auto text-xs font-bold text-gray-400">{coaches.length} total</span>
                )}
              </div>

              {/* Stat tiles */}
              <div className="flex shrink-0 items-stretch divide-x divide-gray-100 border-b border-gray-200">
                {[
                  { key: 'arrived',    label: 'Arrived',     value: arrived.length,    color: 'text-green-600'  },
                  { key: 'notArrived', label: 'Not Arrived', value: notArrived.length, color: 'text-yellow-500' },
                  { key: 'walkIn',     label: 'Walk-ins',    value: walkIns.length,    color: 'text-blue-500'   },
                ].map(({ key, label, value, color }) => (
                  <button
                    key={key}
                    data-testid={`coach-expand-${key}`}
                    onClick={() => setCoachExpanded(s => s === key ? null : key)}
                    className="flex-1 flex flex-col items-center py-2 gap-0.5 hover:bg-gray-50 transition-colors"
                  >
                    <div className={`text-2xl font-black tabular-nums leading-none ${color}`}>{value}</div>
                    <span className={`text-[9px] font-semibold uppercase tracking-wide ${color}`}>{label}</span>
                    <ChevronDown className={`w-3 h-3 text-gray-300 transition-transform ${coachExpanded === key ? 'rotate-180' : ''}`} />
                  </button>
                ))}
              </div>

              {/* Coach list */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {coaches.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 p-4">
                    <UserCheck className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-xs text-center">No coaches found for this competition</p>
                  </div>
                ) : (
                  <>
                    {/* Show expanded section or all coaches */}
                    {(coachExpanded ? sectionCoaches(coachExpanded) : coaches).map(coach => {
                      const isToggling = togglingCoach === coach.id;
                      const checkedInTime = coach.checkedInAt
                        ? new Date(coach.checkedInAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
                        : null;
                      return (
                        <div
                          key={coach.id}
                          data-testid={`coach-row-${coach.id}`}
                          className="flex items-start justify-between px-3 py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50 gap-2"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-bold text-gray-900 truncate">
                                {coach.firstName} {coach.lastName}
                              </span>
                              {coach.walkin && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 shrink-0">
                                  WALK-IN
                                </span>
                              )}
                              {coach.checkedIn && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-600 shrink-0">
                                  ✓
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 truncate block">{coach.clubName || '—'}</span>
                            {/* Walk-in details */}
                            {coach.walkin && (coach.bgNumber || coach.qualification) && (
                              <div className="flex gap-2 mt-0.5 flex-wrap">
                                {coach.bgNumber && (
                                  <span className="text-[9px] text-gray-400">BG: {coach.bgNumber}</span>
                                )}
                                {coach.qualification && (
                                  <span className="text-[9px] text-gray-400">{coach.qualification}</span>
                                )}
                              </div>
                            )}
                            {checkedInTime && (
                              <span className="text-[9px] text-gray-400">{checkedInTime}</span>
                            )}
                          </div>
                          <div className="shrink-0">
                            {coach.checkedIn ? (
                              <button
                                data-testid={`undo-coach-checkin-${coach.id}`}
                                onClick={() => handleUndoCoachCheckIn(coach)}
                                disabled={isToggling}
                                className="text-[10px] font-semibold px-2 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-100 hover:border-gray-400 disabled:opacity-40 transition-colors"
                              >
                                {isToggling ? '...' : 'Undo'}
                              </button>
                            ) : (
                              <button
                                data-testid={`check-in-coach-${coach.id}`}
                                onClick={() => handleCoachCheckIn(coach)}
                                disabled={isToggling}
                                className="text-[10px] font-semibold px-2 py-1 rounded border border-green-200 text-green-600 hover:bg-green-50 hover:border-green-400 disabled:opacity-40 transition-colors"
                              >
                                {isToggling ? '...' : 'Check In'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {coachExpanded && sectionCoaches(coachExpanded).length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-4">None</p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })()}

      </div>
    </div>
  );
}


